import React from "react";
import { Box, Typography, IconButton, Grid, Tabs, Tab } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import AssemblyBox from "./AssemblyBox";
import { getAssemblyGroupFacets } from "../../../services/tecdocService";
import { VehicleProductGroup } from "../../../interfaces/VehicleProductGroup";
import AssemblyTree from "./AssemblyTree";
import ShortcutAssemblies from "./ShortcutAssemblies";
import ProductBrands from "../../products/ProductBrands";
import { DataSupplierFacet } from "../../../interfaces/DataSupplierFacet";
import SearchLiveAssemblyGroups from "./SearchLiveAssemblyGroups";
import { changeGridView, getUser } from "../../../services/core-api";
import VinalfaDiagramPanel from "./VinalfaDiagramPanel";
import OemTecdocResults from "./OemTecdocResults";

export type AssembliesTab = "tecdoc" | "diagram";

type Props = {
  activeTab?: AssembliesTab;
  onTabChange?: (tab: AssembliesTab) => void;
  /** Hide outer vehicle column chrome when page already shows VehicleDetail */
  workspaceLayout?: boolean;
  showShortcuts?: boolean;
};

const Assemblies = ({
  activeTab: controlledTab,
  onTabChange,
  workspaceLayout = false,
  showShortcuts = true,
}: Props) => {
  const theme = useTheme();
  const [internalTab, setInternalTab] = React.useState<AssembliesTab>("tecdoc");
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: AssembliesTab) => {
    if (onTabChange) onTabChange(tab);
    else setInternalTab(tab);
  };

  const [isGridView, setIsGridView] = React.useState(true);
  const [isTreeView, setIsTreeView] = React.useState(false);
  const [vehicleProductGroups, setVehicleProductGroups] = React.useState<
    VehicleProductGroup[] | null
  >(null);
  const [linkageTargetId, setLinkageTargetId] = React.useState<number | null>(
    null
  );
  const [linkageTargetType, setLinkageTargetType] = React.useState<
    string | null
  >(null);
  const [brandId, setBrandId] = React.useState<number | null>(null);
  const [dataSupplierFacets, setDataSupplierFacets] =
    React.useState<DataSupplierFacet | null>(null);
  const [vehicleHash, setVehicleHash] = React.useState<string | null>(null);
  const [oemKeyword, setOemKeyword] = React.useState<string>("");

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const linkageTargetTypeParam = urlParams.get("linkageTargetType");
    const linkageTargetIdParam = urlParams.get("linkageTargetId");
    const brandIdParam = urlParams.get("brand");
    const vehicleHashParam = urlParams.get("vehicleHash");

    if (linkageTargetIdParam) {
      setLinkageTargetId(parseInt(linkageTargetIdParam, 10));
    }
    if (linkageTargetTypeParam) {
      setLinkageTargetType(linkageTargetTypeParam);
    }
    if (brandIdParam) {
      setBrandId(parseInt(brandIdParam, 10));
    }
    if (vehicleHashParam) {
      setVehicleHash(vehicleHashParam);
      // Prefer Diagram tab when arriving from VIN / OEM flow
      if (controlledTab === undefined) {
        setInternalTab("diagram");
      }
    }
  }, [controlledTab]);

  React.useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = localStorage.getItem("user");
        if (user) {
          const userData = JSON.parse(user);
          const authUser = await getUser(userData.id);
          if (authUser) {
            setIsGridView(authUser.activate_grid_view);
            setIsTreeView(!authUser.activate_grid_view);
          }
        }
      } catch (error) {}
    };

    fetchUserData();
  }, []);

  React.useEffect(() => {
    if (linkageTargetId !== null && linkageTargetType !== null) {
      fetchAssemblyGroups(linkageTargetType, linkageTargetId);
    }
  }, [linkageTargetId, linkageTargetType]);

  const fetchAssemblyGroups = async (type: string, id: number) => {
    try {
      const options: { brand?: number } = {};
      if (brandId !== null) {
        options.brand = brandId;
      }

      const response = await getAssemblyGroupFacets(type, id, options);
      if (response) {
        setVehicleProductGroups(response.assemblyGroupFacets.counts);
        setDataSupplierFacets(response.dataSupplierFacets);
      }
    } catch (error) {}
  };

  const handleViewChange = async (view: string) => {
    if (view === "tree") {
      try {
        const response = await changeGridView();
        if (response) {
          setIsGridView(false);
          setIsTreeView(true);
        }
      } catch (error) {}
    } else {
      setIsGridView(true);
      setIsTreeView(false);
    }
  };

  const handleOemClick = (oem: string) => {
    setOemKeyword(oem);
  };

  React.useEffect(() => {
    if (activeTab !== "diagram") {
      setOemKeyword("");
    }
  }, [activeTab]);

  return (
    <>
      <Box
        sx={{
          bgcolor: "#f1f1f1",
          p: 1.1,
          borderTop: `2px solid ${theme.palette.primary.dark}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "15px",
              textAlign: "left",
            }}
          >
            Product Groups
          </Typography>
          {activeTab === "tecdoc" && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "15px",
                  textAlign: "left",
                  mr: 2,
                }}
              >
                Dashboard
              </Typography>
              {isGridView ? (
                <IconButton
                  sx={{ color: `${theme.palette.secondary.main}` }}
                  onClick={() => handleViewChange("grid")}
                >
                  <GridViewOutlinedIcon />
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  onClick={() => handleViewChange("grid")}
                >
                  <GridViewOutlinedIcon />
                </IconButton>
              )}

              {isTreeView ? (
                <IconButton
                  sx={{ color: `${theme.palette.secondary.main}` }}
                  onClick={() => handleViewChange("tree")}
                >
                  <MenuOutlinedIcon />
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  onClick={() => handleViewChange("tree")}
                >
                  <MenuOutlinedIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {showShortcuts && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ShortcutAssemblies />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box
        sx={{
          minHeight: 352,
          minWidth: 250,
          paddingLeft: workspaceLayout ? 1 : 4,
          paddingRight: workspaceLayout ? 1 : 4,
          paddingBottom: 4,
          border: "1px solid #f1f1f1",
        }}
      >
        <Box sx={{ pt: 2, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_e, value: AssembliesTab) => setActiveTab(value)}
              indicatorColor="primary"
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: 14,
                  color: theme.palette.text.primary,
                  opacity: 1,
                  "&.Mui-selected": {
                    color: theme.palette.primary.main,
                  },
                },
              }}
            >
              <Tab label="TecDoc groups" value="tecdoc" />
              <Tab label="Diagram" value="diagram" />
            </Tabs>
            {activeTab === "tecdoc" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  ml: "auto",
                }}
              >
                <Box sx={{ width: { xs: 160, sm: 200 } }}>
                  <SearchLiveAssemblyGroups />
                </Box>
                <Box sx={{ width: { xs: 160, sm: 200 } }}>
                  <ProductBrands dataSupplierFacets={dataSupplierFacets} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {activeTab === "diagram" ? (
          <>
            <VinalfaDiagramPanel
              workspaceLayout={workspaceLayout || !!vehicleHash}
              onOemClick={handleOemClick}
            />
            {oemKeyword && (
              <OemTecdocResults
                keyword={oemKeyword}
                linkageTargetId={linkageTargetId}
                linkageTargetType={linkageTargetType}
              />
            )}
          </>
        ) : vehicleProductGroups ? (
          isGridView ? (
            <Grid container spacing={2}>
              {vehicleProductGroups.map((group: VehicleProductGroup) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={group.assemblyGroupNodeId}
                >
                  <AssemblyBox group={group} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <AssemblyTree group={vehicleProductGroups} />
          )
        ) : (
          <div>No data available</div>
        )}
      </Box>
    </>
  );
};

export default Assemblies;
